/**
 * JourneyAnimation — Successlane Main Street (v4)
 * ------------------------------------------------
 * Horizontal main street with 5 buildings aligned in workflow order, right
 * along the road. A character walks left → right, literally entering each
 * building's door (door slides open, character walks in, door closes for
 * ~1.5s while the building animates, door opens again, character walks out).
 *
 * Special at Building 4 (Recruiter Certification): balloons fly up + confetti.
 *
 * Each building has a step-description label that fades in when the character
 * arrives and fades out when they leave.
 *
 * SUCCESSLANE billboard sits right next to Building 3 (Expert HQ).
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

// ─── Tokens ─────────────────────────────────────────────────────────────────
const C = {
  skyTop: '#FEFCE8', skyBot: '#E8F1FC',
  groundTop: '#FCF4D3', groundBot: '#E2DFB4',
  sidewalk: '#F4EDD8',
  road: '#E0E2E6', roadEdge: '#C4C6CB',
  buildWall: '#FFFFFF', buildSide: '#F2F4F7',
  buildAccent: '#7DD3C0', buildAccentDark: '#5BAFA2',
  buildOutline: '#0F2E4D',
  windowGlass: '#CFE8DC',
  windowGlow: '#FBBF24',
  navy: '#0D1F3C',
  primary: '#1A6FD6', primaryLight: '#E8F1FC',
  gold: '#D4A742', green: '#10B981', greenSoft: '#86EFAC',
  red: '#E04E3A',
  charBlue: '#2D54A5',
  charSkin: '#F3D0AC', charHair: '#3D2A20', charShoe: '#3A2010',
  treeMid: '#74C797', treeDark: '#3D8A60', treeTrunk: '#7B5237',
  lampPole: '#33485E',
  ped1: '#7E4FB8', ped2: '#3B82F6', ped3: '#FB923C', ped4: '#0EA5E9', ped5: '#EC4899',
  text: '#0D1F3C', textMid: '#475569', textFaint: '#94A3B8',
  border: '#E5E9F0', white: '#FFFFFF',
};

// ─── Layout ─────────────────────────────────────────────────────────────────
const VB_W = 1500;
const VB_H = 760;
const GROUND_Y = 560;   // top of sidewalk (where building bases sit)
const FEET_Y = 555;     // character's feet ride on this line
const ROAD_Y = 660;     // top of road

// Buildings — 5 along the street, each with a clearly defined door at door_x.
//   door_x is the center x of the door.
//   bldX = building's left x edge, bldW = width.
const BUILDINGS = [
  { id: 'eval',      doorX: 175,  bldX: 75,   bldW: 200, h: 240, step: 1,
    title: 'Free Profile Evaluation',
    blurb: 'No credit card. Drop your LinkedIn URL or PDF and get a recruiter-voice score in 60 seconds — exactly what hiring managers see in the 8-second test.',
    accent: C.primary,
    ctaPrimary: { label: 'Try it free', to: '/analyze' },
  },
  { id: 'checkout',  doorX: 430,  bldX: 320,  bldW: 220, h: 160, step: 2,
    title: 'Secure Checkout',
    blurb: 'One-time ₹999 payment via Razorpay. No subscription, no upsells. Order ID gets generated and the rewrite work begins instantly.',
    accent: C.green,
    ctaPrimary: { label: 'Try it free', to: '/analyze' },
  },
  { id: 'expert',    doorX: 760,  bldX: 595,  bldW: 330, h: 300, step: 3,
    title: 'Expert Rewrite',
    blurb: 'Our expert + AI rewrite your Headline, About, and Skills using recruiter-tested patterns and keywords matched to your dream companies.',
    accent: C.navy,
    ctaPrimary: { label: 'Try it free', to: '/analyze' },
  },
  { id: 'recruiter', doorX: 1080, bldX: 970,  bldW: 230, h: 230, step: 4,
    title: 'Recruiter Certification',
    blurb: 'A domain-matched, LinkedIn-verified recruiter reviews your rewrite, makes any needed edits, and signs off — their name appears on your certification.',
    accent: C.gold,
    ctaPrimary: { label: 'Try it free', to: '/analyze' },
  },
  { id: 'delivery',  doorX: 1340, bldX: 1240, bldW: 200, h: 190, step: 5,
    title: 'Delivered to Your Inbox',
    blurb: 'Within 48 hours, the final certified profile arrives in your inbox as an HTML attachment with copy buttons — paste straight into LinkedIn.',
    accent: C.red,
    ctaPrimary: { label: 'Try it free', to: '/analyze' },
  },
];

// ─── PHASES — 4 per building (approach, enter, inside, exit) + reset ────────
// Total: 5 × 4 + 1 = 21 phases.
const PHASES = [];
BUILDINGS.forEach((b, i) => {
  PHASES.push({ b: i, kind: 'approach', dur: 2000 });
  PHASES.push({ b: i, kind: 'enter',    dur: 700 });
  PHASES.push({ b: i, kind: 'inside',   dur: 1600 });
  PHASES.push({ b: i, kind: 'exit',     dur: 700 });
});
PHASES.push({ b: null, kind: 'leave', dur: 1500 });
// Total: ~26.5 s loop

// Target x position of the character for a given phase index.
const charTargetX = (phaseIdx) => {
  const p = PHASES[phaseIdx];
  if (!p) return 60;
  if (p.kind === 'leave') return VB_W + 60;
  if (p.kind === 'approach') return BUILDINGS[p.b].doorX - 15; // arrive at door's left side
  if (p.kind === 'enter')    return BUILDINGS[p.b].doorX;       // walk INTO door centre
  if (p.kind === 'inside')   return BUILDINGS[p.b].doorX;       // stay inside (hidden by door)
  if (p.kind === 'exit')     return BUILDINGS[p.b].doorX + 25;  // walk OUT past door
  return 60;
};

// ─── Walking Character ──────────────────────────────────────────────────────
const Character = ({ walking = true }) => (
  <motion.g
    animate={walking ? { y: [0, -1, 0, -1, 0] } : { y: 0 }}
    transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
  >
    <ellipse cx={0} cy={9} rx={9} ry={2.4} fill={C.navy} opacity={0.18} />
    <motion.g
      animate={walking ? { rotate: [-22, 22, -22] } : { rotate: 0 }}
      transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: '0px', originY: '-2px' }}
    >
      <rect x={-4} y={-2} width={3.4} height={10} rx={1.3} fill={C.charBlue} stroke={C.buildOutline} strokeWidth={0.6} />
      <rect x={-4} y={6}  width={4.5} height={3} rx={1.2} fill={C.charShoe} />
    </motion.g>
    <motion.g
      animate={walking ? { rotate: [22, -22, 22] } : { rotate: 0 }}
      transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: '0px', originY: '-2px' }}
    >
      <rect x={0.5} y={-2} width={3.4} height={10} rx={1.3} fill={C.charBlue} stroke={C.buildOutline} strokeWidth={0.6} />
      <rect x={-0.5} y={6} width={4.5} height={3} rx={1.2} fill={C.charShoe} />
    </motion.g>
    <rect x={-5.5} y={-15} width={11} height={14} rx={2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.7} />
    <rect x={-3.4} y={-15} width={1.4} height={9} fill={C.charBlue} />
    <rect x={2.0}  y={-15} width={1.4} height={9} fill={C.charBlue} />
    <motion.g
      animate={walking ? { rotate: [22, -22, 22] } : { rotate: 0 }}
      transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: '-5px', originY: '-14px' }}
    >
      <rect x={-7.5} y={-14} width={3} height={9} rx={1.2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
      <circle cx={-6} cy={-5} r={1.6} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
    </motion.g>
    <motion.g
      animate={walking ? { rotate: [-22, 22, -22] } : { rotate: 0 }}
      transition={{ duration: 0.45, repeat: Infinity, ease: 'easeInOut' }}
      style={{ originX: '5px', originY: '-14px' }}
    >
      <rect x={4.5} y={-14} width={3} height={9} rx={1.2} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
      <circle cx={6} cy={-5} r={1.6} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
    </motion.g>
    <circle cx={0} cy={-21} r={5.5} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.6} />
    <path d={`M -5 -25 Q 0 -29 5 -25 L 4 -22 L -4 -22 Z`} fill={C.charHair} stroke={C.buildOutline} strokeWidth={0.5} />
    <circle cx={-1.7} cy={-21} r={0.7} fill={C.buildOutline} />
    <circle cx={1.7}  cy={-21} r={0.7} fill={C.buildOutline} />
    <path d={`M -5 -25 Q 0 -29 5 -25 L 5 -23 L -5 -23 Z`} fill={C.red} stroke={C.buildOutline} strokeWidth={0.6} />
    <circle cx={0} cy={-25.5} r={1.4} fill={C.white} stroke={C.buildOutline} strokeWidth={0.5} />
  </motion.g>
);

// ─── Pedestrian (small, idle bystander) ─────────────────────────────────────
const Pedestrian = ({ x, y, color = C.ped1, scale = 1 }) => (
  <motion.g
    transform={`translate(${x}, ${y}) scale(${scale})`}
    animate={{ y: [0, -0.6, 0] }}
    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
  >
    <ellipse cx={0} cy={6} rx={6.5} ry={1.6} fill={C.navy} opacity={0.18} />
    <rect x={-3} y={-2} width={2.4} height={7} rx={1} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.5} />
    <rect x={0.6} y={-2} width={2.4} height={7} rx={1} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.5} />
    <rect x={-4} y={-12} width={8} height={11} rx={2} fill={color} stroke={C.buildOutline} strokeWidth={0.6} />
    <rect x={-5} y={-11} width={2} height={8} rx={1} fill={color} stroke={C.buildOutline} strokeWidth={0.5} />
    <rect x={3} y={-11} width={2} height={8} rx={1} fill={color} stroke={C.buildOutline} strokeWidth={0.5} />
    <circle cx={0} cy={-16} r={4} fill={C.charSkin} stroke={C.buildOutline} strokeWidth={0.5} />
    <path d={`M -4 -19 Q 0 -22 4 -19 L 4 -17 L -4 -17 Z`} fill={C.charHair} />
  </motion.g>
);

// ─── Generic Building Door — slides open/closed ─────────────────────────────
// The door has two panels (left + right) that slide outward when open.
// When `open` is true, the panels are apart so the character can walk through.
const BuildingDoor = ({ doorX, doorY, open, accent = C.primary }) => {
  const w = 28, h = 52; // total door size (closed)
  const panelW = w / 2;
  return (
    <g>
      {/* Doorframe */}
      <rect x={doorX - w/2 - 2} y={doorY - h - 2} width={w + 4} height={h + 4} fill={accent} stroke={C.buildOutline} strokeWidth={1} />
      {/* Inner dark recess (visible when door open) */}
      <rect x={doorX - w/2} y={doorY - h} width={w} height={h} fill={C.navy} opacity={0.25} />
      {/* Left panel */}
      <motion.rect
        x={doorX - w/2} y={doorY - h} width={panelW} height={h}
        fill={C.buildWall} stroke={C.buildOutline} strokeWidth={0.9}
        animate={{ x: open ? doorX - w/2 - panelW + 1 : doorX - w/2 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      />
      {/* Right panel */}
      <motion.rect
        x={doorX} y={doorY - h} width={panelW} height={h}
        fill={C.buildWall} stroke={C.buildOutline} strokeWidth={0.9}
        animate={{ x: open ? doorX + panelW - 1 : doorX }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
      />
      {/* Door knobs */}
      <motion.circle
        animate={{ cx: open ? doorX - w/2 - panelW + 1 + panelW - 3 : doorX - 3 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        cy={doorY - h/2} r={1.4} fill={C.gold}
      />
      <motion.circle
        animate={{ cx: open ? doorX + panelW - 1 + 3 : doorX + 3 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        cy={doorY - h/2} r={1.4} fill={C.gold}
      />
      {/* Welcome mat */}
      <rect x={doorX - w/2 - 4} y={doorY - 2} width={w + 8} height={4} rx={1} fill={accent} opacity={0.5} />
    </g>
  );
};

// ─── Pulsing window helper ──────────────────────────────────────────────────
const PulseWindow = ({ x, y, w = 18, h = 18, delay = 0 }) => (
  <motion.rect
    x={x} y={y} width={w} height={h} rx={1.5}
    fill={C.buildAccent}
    stroke={C.buildOutline} strokeWidth={0.7}
    animate={{ fill: [C.buildAccent, C.windowGlow, C.buildAccent] }}
    transition={{ duration: 3.2, repeat: Infinity, delay, ease: 'easeInOut' }}
  />
);

// ─── BUILDINGS — each is a function that returns its SVG group ──────────────

const EvalBuilding = ({ doorOpen, onClick }) => {
  const { doorX, bldX, bldW, h } = BUILDINGS[0];
  const bottomY = GROUND_Y;
  const topY = bottomY - h;
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Shadow */}
      <ellipse cx={doorX} cy={bottomY + 6} rx={bldW / 1.7} ry={5} fill={C.navy} opacity={0.1} />
      {/* Main body */}
      <rect x={bldX} y={topY} width={bldW} height={h} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.4} />
      {/* Top accent stripe */}
      <rect x={bldX} y={topY} width={bldW} height={14} fill={C.primary} stroke={C.buildOutline} strokeWidth={1} />
      {/* Roof */}
      <rect x={bldX - 6} y={topY - 8} width={bldW + 12} height={10} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={1} />
      {/* Score gauge on top */}
      <g transform={`translate(${doorX}, ${topY - 32})`}>
        <circle cx={0} cy={0} r={14} fill={C.white} stroke={C.buildOutline} strokeWidth={1} />
        <motion.circle cx={0} cy={0} r={14}
          fill="none" stroke={C.primary} strokeWidth={4} strokeLinecap="round"
          strokeDasharray={2 * Math.PI * 14}
          animate={{ strokeDashoffset: [2 * Math.PI * 14, 2 * Math.PI * 14 * 0.18, 2 * Math.PI * 14 * 0.18, 2 * Math.PI * 14] }}
          transition={{ duration: 7, repeat: Infinity, times: [0, 0.4, 0.7, 1], ease: 'easeOut' }}
          transform="rotate(-90)"
        />
        <text x={0} y={4} textAnchor="middle" fontSize={11} fontWeight={800} fill={C.navy}>82</text>
      </g>
      {/* Windows */}
      {[0,1,2].map(r => (
        <g key={r}>
          {[0,1,2].map(c => (
            <PulseWindow key={c} x={bldX + 20 + c * 50} y={topY + 30 + r * 50} w={36} h={32} delay={(r*3+c)*0.3} />
          ))}
        </g>
      ))}
      {/* Door — at center bottom */}
      <BuildingDoor doorX={doorX} doorY={bottomY} open={doorOpen} accent={C.primary} />
    </g>
  );
};

const CheckoutBuilding = ({ doorOpen, onClick }) => {
  const { doorX, bldX, bldW, h } = BUILDINGS[1];
  const bottomY = GROUND_Y;
  const topY = bottomY - h;
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <ellipse cx={doorX} cy={bottomY + 6} rx={bldW / 1.7} ry={5} fill={C.navy} opacity={0.1} />
      <rect x={bldX} y={topY} width={bldW} height={h} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.4} />
      <rect x={bldX} y={topY} width={bldW} height={14} fill={C.green} stroke={C.buildOutline} strokeWidth={1} />
      <rect x={bldX - 6} y={topY - 8} width={bldW + 12} height={10} fill={C.greenSoft} stroke={C.buildOutline} strokeWidth={1} />
      {/* Awning over the door */}
      <path d={`M ${doorX - 60} ${bottomY - 60} L ${doorX - 50} ${bottomY - 80} L ${doorX + 50} ${bottomY - 80} L ${doorX + 60} ${bottomY - 60} Z`} fill={C.green} stroke={C.buildOutline} strokeWidth={1} />
      {/* ₹999 sign */}
      <rect x={doorX - 32} y={bottomY - 78} width={64} height={20} rx={3} fill={C.navy} stroke={C.buildOutline} strokeWidth={1} />
      <text x={doorX} y={bottomY - 64} textAnchor="middle" fontSize={13} fontWeight={800} fill={C.white}>₹999</text>
      {/* Side windows */}
      <PulseWindow x={bldX + 20} y={topY + 40} w={28} h={22} delay={0.5} />
      <PulseWindow x={bldX + bldW - 48} y={topY + 40} w={28} h={22} delay={1.5} />
      <BuildingDoor doorX={doorX} doorY={bottomY} open={doorOpen} accent={C.green} />
    </g>
  );
};

const ExpertBuilding = ({ doorOpen, onClick }) => {
  const { doorX, bldX, bldW, h } = BUILDINGS[2];
  const bottomY = GROUND_Y;
  const topY = bottomY - h;
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <ellipse cx={doorX} cy={bottomY + 8} rx={bldW / 1.7} ry={6} fill={C.navy} opacity={0.12} />
      <rect x={bldX} y={topY} width={bldW} height={h} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.5} />
      <rect x={bldX} y={topY} width={bldW} height={16} fill={C.navy} />
      {/* Roof line accent */}
      <rect x={bldX - 6} y={topY - 12} width={bldW + 12} height={14} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={1} />
      {/* Successlane sign on roof */}
      <rect x={bldX + 50} y={topY - 32} width={bldW - 100} height={20} rx={3} fill={C.navy} stroke={C.buildOutline} strokeWidth={1} />
      <circle cx={bldX + 65} cy={topY - 22} r={3} fill={C.gold} />
      <text x={bldX + bldW / 2 + 10} y={topY - 17} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.white} fontFamily="'Fraunces', serif" style={{ letterSpacing: '0.05em' }}>
        SUCCESSLANE
      </text>
      {/* Window grid */}
      {[0,1,2,3].map(r => (
        <g key={r}>
          {[0,1,2,3,4].map(c => (
            <PulseWindow key={c} x={bldX + 25 + c * 60} y={topY + 30 + r * 60} w={42} h={42} delay={(r*5+c)*0.25} />
          ))}
        </g>
      ))}
      {/* Door */}
      <BuildingDoor doorX={doorX} doorY={bottomY} open={doorOpen} accent={C.navy} />
    </g>
  );
};

const RecruiterBuilding = ({ doorOpen, celebrating, onClick }) => {
  const { doorX, bldX, bldW, h } = BUILDINGS[3];
  const bottomY = GROUND_Y;
  const topY = bottomY - h;
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <ellipse cx={doorX} cy={bottomY + 7} rx={bldW / 1.7} ry={5} fill={C.navy} opacity={0.1} />
      <rect x={bldX} y={topY} width={bldW} height={h} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.4} />
      <rect x={bldX} y={topY + 36} width={bldW} height={6} fill={C.gold} />
      {/* Dome roof */}
      <path d={`M ${bldX - 4} ${topY + 36} Q ${doorX} ${topY - 24} ${bldX + bldW + 4} ${topY + 36} Z`} fill={C.buildAccentDark} stroke={C.buildOutline} strokeWidth={1.2} />
      {/* CERT badge */}
      <g transform={`translate(${doorX}, ${topY + 70})`}>
        <rect x={-44} y={-13} width={88} height={26} rx={4} fill={C.greenSoft} stroke={C.green} strokeWidth={1.4} />
        <circle cx={-26} cy={0} r={8} fill={C.green} />
        <path d="M -30 0 L -27 3 L -22 -2" stroke={C.white} strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <text x={6} y={4} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.green}>CERT</text>
      </g>
      {/* Columns */}
      {[0,1,2,3].map(i => (
        <rect key={i} x={bldX + 20 + i * 55} y={topY + 110} width={6} height={h - 110 - 12} fill={C.buildSide} stroke={C.buildOutline} strokeWidth={0.7} />
      ))}
      {/* Sparkles on top */}
      {celebrating && [0,1,2,3,4,5].map(i => (
        <motion.circle key={i}
          cx={doorX - 80 + i * 30} cy={topY - 10}
          r={2} fill={C.gold}
          animate={{ scale: [0, 1.6, 0], opacity: [0, 1, 0] }}
          transition={{ duration: 1.6, delay: i * 0.18, repeat: Infinity, repeatDelay: 0.4 }}
        />
      ))}
      <BuildingDoor doorX={doorX} doorY={bottomY} open={doorOpen} accent={C.gold} />
    </g>
  );
};

const DeliveryBuilding = ({ doorOpen, onClick }) => {
  const { doorX, bldX, bldW, h } = BUILDINGS[4];
  const bottomY = GROUND_Y;
  const topY = bottomY - h;
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <ellipse cx={doorX} cy={bottomY + 6} rx={bldW / 1.7} ry={5} fill={C.navy} opacity={0.1} />
      <rect x={bldX} y={topY + 30} width={bldW} height={h - 30} fill={C.buildWall} stroke={C.buildOutline} strokeWidth={1.4} />
      {/* Pitched roof */}
      <path d={`M ${bldX - 4} ${topY + 30} L ${doorX} ${topY - 20} L ${bldX + bldW + 4} ${topY + 30} Z`} fill={C.red} stroke={C.buildOutline} strokeWidth={1.2} />
      {/* Antenna */}
      <rect x={doorX - 1.5} y={topY - 50} width={3} height={32} fill={C.navy} />
      <circle cx={doorX} cy={topY - 52} r={3} fill={C.gold} />
      <motion.g
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <path d={`M ${doorX - 12} ${topY - 48} Q ${doorX} ${topY - 60} ${doorX + 12} ${topY - 48}`} stroke={C.primary} strokeWidth={1.4} fill="none" strokeLinecap="round" />
        <path d={`M ${doorX - 18} ${topY - 52} Q ${doorX} ${topY - 70} ${doorX + 18} ${topY - 52}`} stroke={C.primary} strokeWidth={1.2} fill="none" strokeLinecap="round" opacity={0.6} />
      </motion.g>
      {/* Mailbox slot above door */}
      <rect x={doorX - 28} y={bottomY - 84} width={56} height={8} rx={2} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.8} />
      {/* Letter icon */}
      <rect x={bldX + 18} y={topY + 60} width={26} height={18} fill={C.white} stroke={C.primary} strokeWidth={1} />
      <path d={`M ${bldX + 18} ${topY + 60} L ${bldX + 31} ${topY + 70} L ${bldX + 44} ${topY + 60}`} fill="none" stroke={C.primary} strokeWidth={1} />
      <PulseWindow x={bldX + bldW - 50} y={topY + 60} w={28} h={20} delay={0.4} />
      <BuildingDoor doorX={doorX} doorY={bottomY} open={doorOpen} accent={C.red} />
    </g>
  );
};

// ─── SUCCESSLANE Billboard (tall pole, sign panel) — next to Expert HQ ──────
const SuccesslaneBillboard = () => {
  const x = 590; // just to the left of Expert HQ which starts at x=595
  const yTop = 180;
  const yBot = GROUND_Y;
  return (
    <g>
      {/* Pole */}
      <rect x={x - 1.5} y={yTop + 50} width={3} height={yBot - yTop - 50} fill={C.lampPole} stroke={C.buildOutline} strokeWidth={0.7} />
      {/* Sign panel */}
      <rect x={x - 70} y={yTop} width={140} height={50} rx={4} fill={C.navy} stroke={C.buildOutline} strokeWidth={1.2} />
      <rect x={x - 66} y={yTop + 4} width={132} height={42} rx={2} fill={C.primary} />
      <circle cx={x - 50} cy={yTop + 22} r={6} fill={C.gold} />
      <text x={x + 8} y={yTop + 22} textAnchor="middle" fontSize={13} fontWeight={700} fill={C.white} fontFamily="'Fraunces', serif" style={{ letterSpacing: '0.06em' }}>SUCCESSLANE</text>
      <text x={x + 8} y={yTop + 38} textAnchor="middle" fontSize={8} fontWeight={600} fill={C.white} opacity={0.85}>RECRUITER-CERTIFIED REWRITE</text>
    </g>
  );
};

// ─── Decorative pieces (trees, lamp, etc.) ──────────────────────────────────
const PalmTree = ({ x, y, scale = 1 }) => (
  <g transform={`translate(${x}, ${y}) scale(${scale})`}>
    <path d="M -2 18 Q -3 8 -1 -8 L 1 -8 Q 3 8 2 18 Z" fill={C.treeTrunk} stroke={C.buildOutline} strokeWidth={0.8} />
    {[-90, -55, -15, 25, 65, 105].map((rot, i) => (
      <g key={i} transform={`rotate(${rot})`}>
        <path d="M 0 -10 Q 12 -16 24 -12 Q 14 -10 0 -10 Z" fill={C.treeMid} stroke={C.buildOutline} strokeWidth={0.7} />
      </g>
    ))}
    <circle cx={0} cy={-10} r={2.5} fill={C.treeDark} />
    <circle cx={2} cy={-7} r={1.5} fill={C.charShoe} stroke={C.buildOutline} strokeWidth={0.5} />
  </g>
);

const RoundTree = ({ x, y, scale = 1 }) => (
  <g transform={`translate(${x}, ${y}) scale(${scale})`}>
    <rect x={-2.5} y={0} width={5} height={14} fill={C.treeTrunk} stroke={C.buildOutline} strokeWidth={0.7} />
    <circle cx={0} cy={-6} r={16} fill={C.treeMid} stroke={C.buildOutline} strokeWidth={1} />
    <circle cx={-7} cy={-3} r={8} fill={C.treeDark} opacity={0.55} />
    <circle cx={6} cy={-10} r={7} fill={C.treeDark} opacity={0.45} />
  </g>
);

const StreetLamp = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect x={-1} y={-32} width={2} height={32} fill={C.lampPole} stroke={C.buildOutline} strokeWidth={0.6} />
    <ellipse cx={0} cy={-36} rx={4} ry={3.5} fill="#FFF3C8" stroke={C.buildOutline} strokeWidth={0.7} />
  </g>
);

const Motorbike = ({ x, y, color = C.red }) => (
  <g transform={`translate(${x}, ${y})`}>
    <ellipse cx={0} cy={5} rx={16} ry={2} fill={C.navy} opacity={0.18} />
    <circle cx={-9} cy={0} r={4} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.6} />
    <circle cx={-9} cy={0} r={2} fill={C.buildSide} />
    <circle cx={9}  cy={0} r={4} fill={C.navy} stroke={C.buildOutline} strokeWidth={0.6} />
    <circle cx={9}  cy={0} r={2} fill={C.buildSide} />
    <path d="M -9 -2 L 5 -8 L 12 -4 L 9 -2 Z" fill={color} stroke={C.buildOutline} strokeWidth={0.7} />
    <rect x={-4} y={-9} width={9} height={2.5} rx={1} fill={C.navy} />
    <rect x={4} y={-15} width={1.2} height={9} fill={C.navy} />
    <rect x={2} y={-16} width={6} height={2} rx={1} fill={C.navy} />
  </g>
);

const ChargingStation = ({ x, y }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect x={-8} y={-30} width={16} height={30} rx={3} fill={C.buildAccent} stroke={C.buildOutline} strokeWidth={1} />
    <rect x={-5} y={-26} width={10} height={8} rx={1.5} fill={C.navy} />
    <motion.rect x={-3.5} y={-24.5} width={3} height={5} fill={C.green}
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
    />
    <path d="M -1.5 -16 L -3 -10 L 0 -10 L -1.5 -4 L 2 -12 L -1 -12 Z" fill={C.gold} stroke={C.buildOutline} strokeWidth={0.5} />
    <path d="M 6 -14 Q 12 -8 14 -2 Q 14 2 12 4" fill="none" stroke={C.navy} strokeWidth={1.4} strokeLinecap="round" />
    <rect x={11} y={3} width={4} height={3} rx={1} fill={C.navy} />
  </g>
);

const Car = ({ x, y, color = C.primary }) => (
  <g transform={`translate(${x}, ${y})`}>
    <ellipse cx={0} cy={9} rx={22} ry={3} fill={C.navy} opacity={0.18} />
    <rect x={-22} y={-2} width={44} height={9} rx={3} fill={color} stroke={C.buildOutline} strokeWidth={0.8} />
    <path d="M -16 -2 L -10 -10 L 10 -10 L 16 -2 Z" fill={color} stroke={C.buildOutline} strokeWidth={0.8} />
    <path d="M -14 -3 L -10 -9 L -1 -9 L -1 -3 Z" fill={C.primaryLight} stroke={C.buildOutline} strokeWidth={0.5} />
    <path d="M 1 -3 L 1 -9 L 10 -9 L 14 -3 Z" fill={C.primaryLight} stroke={C.buildOutline} strokeWidth={0.5} />
    <circle cx={-13} cy={7} r={3} fill={C.navy} />
    <circle cx={-13} cy={7} r={1.4} fill={C.buildSide} />
    <circle cx={13} cy={7}  r={3} fill={C.navy} />
    <circle cx={13} cy={7}  r={1.4} fill={C.buildSide} />
    <circle cx={20} cy={2} r={1.2} fill={C.gold} />
  </g>
);

// ─── Step Label (above each building, fades in/out based on character) ─────
const StepLabel = ({ b, visible }) => (
  <motion.g
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 14 }}
    transition={{ duration: 0.45, ease: 'easeOut' }}
    style={{ pointerEvents: 'none' }}
  >
    <rect
      x={b.doorX - 100} y={GROUND_Y - b.h - 64} width={200} height={32} rx={16}
      fill={C.white} stroke={b.accent} strokeWidth={1.6}
    />
    {/* Step badge circle */}
    <circle cx={b.doorX - 80} cy={GROUND_Y - b.h - 48} r={11} fill={b.accent} />
    <text x={b.doorX - 80} y={GROUND_Y - b.h - 44} textAnchor="middle" fontSize={11} fontWeight={800} fill={C.white} fontFamily="'Plus Jakarta Sans', sans-serif">
      {b.step}
    </text>
    <text x={b.doorX + 4} y={GROUND_Y - b.h - 44} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.text} fontFamily="'Plus Jakarta Sans', sans-serif">
      {b.title}
    </text>
    {/* Pointer triangle (label looks like a speech bubble pointing at building roof) */}
    <path d={`M ${b.doorX - 6} ${GROUND_Y - b.h - 32} L ${b.doorX} ${GROUND_Y - b.h - 22} L ${b.doorX + 6} ${GROUND_Y - b.h - 32} Z`} fill={C.white} stroke={b.accent} strokeWidth={1.6} />
    <line x1={b.doorX - 5} y1={GROUND_Y - b.h - 32} x2={b.doorX + 5} y2={GROUND_Y - b.h - 32} stroke={C.white} strokeWidth={2} />
  </motion.g>
);

// ─── Balloons + Confetti at Recruiter ────────────────────────────────────────
const BalloonsAndConfetti = ({ visible }) => {
  if (!visible) return null;
  const { doorX, bldX, bldW, h } = BUILDINGS[3];
  const topY = GROUND_Y - h;
  const colors = [C.red, C.gold, C.primary, C.green, C.ped5, C.ped1, C.ped3];
  return (
    <g>
      {/* Balloons rising */}
      {colors.map((color, i) => {
        const offsetX = -90 + i * 30;
        return (
          <motion.g
            key={`balloon-${i}`}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: [-10, -160], opacity: [0, 1, 1, 0], x: [0, (i % 2 === 0 ? 8 : -8), 0] }}
            transition={{
              duration: 3.5,
              delay: i * 0.18,
              repeat: Infinity,
              repeatDelay: 0.5,
              times: [0, 0.15, 0.8, 1],
              ease: 'easeOut'
            }}
          >
            <g transform={`translate(${doorX + offsetX}, ${topY - 10})`}>
              <ellipse cx={0} cy={0} rx={10} ry={13} fill={color} stroke={C.buildOutline} strokeWidth={0.8} />
              <ellipse cx={-3} cy={-3} rx={3} ry={5} fill={C.white} opacity={0.4} />
              <path d={`M 0 13 L -1 17 L 0 21 L 1 17 Z`} fill={color} />
              <line x1={0} y1={21} x2={0} y2={32} stroke={C.buildOutline} strokeWidth={0.6} />
            </g>
          </motion.g>
        );
      })}
      {/* Confetti burst from top of building */}
      {[...Array(22)].map((_, i) => {
        const angle = (i / 22) * Math.PI * 2;
        const r = 60 + (i % 3) * 30;
        const dx = Math.cos(angle) * r;
        const dy = -Math.abs(Math.sin(angle)) * r * 0.7;
        const confColors = [C.red, C.gold, C.green, C.primary, C.buildAccent, C.ped5];
        return (
          <motion.rect
            key={`conf-${i}`}
            x={doorX} y={topY - 30}
            width={4} height={7} rx={1}
            fill={confColors[i % confColors.length]}
            initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
            animate={{
              x: [0, dx],
              y: [0, dy, dy + 60],
              opacity: [0, 1, 0],
              rotate: [0, 200 * (i % 2 === 0 ? 1 : -1), 360],
            }}
            transition={{ duration: 2.6, ease: 'easeOut', repeat: Infinity, repeatDelay: 1, delay: (i % 6) * 0.05 }}
          />
        );
      })}
    </g>
  );
};

// ─── Info Card ──────────────────────────────────────────────────────────────
const InfoCard = ({ building, onClose, onContinue, onCta }) => {
  if (!building) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{ position: 'absolute', inset: 0, background: 'rgba(13, 31, 60, 0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16, zIndex: 10 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 16, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 16, opacity: 0, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          style={{ background: C.white, borderRadius: 16, padding: '24px 26px', maxWidth: 360, width: 'calc(100% - 32px)', boxShadow: '0 18px 50px rgba(13, 31, 60, 0.28)', position: 'relative' }}
        >
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: C.textFaint, fontSize: 18, lineHeight: 1 }}>×</button>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.primary, textTransform: 'uppercase', marginBottom: 4 }}>STEP {building.step}</div>
          <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: C.navy, fontFamily: "'Fraunces', serif" }}>{building.title}</h3>
          <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.55, color: C.textMid }}>{building.blurb}</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onContinue}
              style={{ flex: 1, padding: '9px 14px', borderRadius: 9, background: C.white, color: C.textMid, border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Continue tour</button>
            <button type="button" onClick={() => onCta(building.ctaPrimary)}
              style={{ flex: 1, padding: '9px 14px', borderRadius: 9, background: C.primary, color: C.white, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{building.ctaPrimary.label} →</button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══ MAIN ═══════════════════════════════════════════════════════════════════
const JourneyAnimation = ({ compact = false, onCtaClick }) => {
  const prefersReducedMotion = useReducedMotion();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [activeBuilding, setActiveBuilding] = useState(null);

  // Drive phase forward unless an info-card is open or reduced motion is set
  useEffect(() => {
    if (prefersReducedMotion || activeBuilding) return;
    const cur = PHASES[phaseIdx];
    const t = setTimeout(() => setPhaseIdx((p) => (p + 1) % PHASES.length), cur.dur);
    return () => clearTimeout(t);
  }, [phaseIdx, prefersReducedMotion, activeBuilding]);

  const cur = PHASES[phaseIdx];
  const targetX = charTargetX(phaseIdx);
  const charVisible = cur.kind !== 'inside';  // hidden behind closed door during 'inside'
  const charWalking = cur.kind === 'approach' || cur.kind === 'enter' || cur.kind === 'exit' || cur.kind === 'leave';

  // Which buildings have their door open right now?
  const doorOpen = (i) => {
    if (cur.b !== i) return false;
    // Door is open during enter (so character can walk in) and exit (so they can walk out)
    return cur.kind === 'enter' || cur.kind === 'exit';
  };

  // Which step label is visible?
  const labelVisible = (i) => {
    if (cur.b !== i) return false;
    return cur.kind === 'approach' || cur.kind === 'enter' || cur.kind === 'inside' || cur.kind === 'exit';
  };

  // Recruiter celebration: balloons + confetti only while character is inside Recruiter (b=3)
  const recruiterCeleb = cur.b === 3 && (cur.kind === 'enter' || cur.kind === 'inside' || cur.kind === 'exit');

  // Top-right step pill: show current step + sub-state
  const pillBuilding = cur.b != null ? BUILDINGS[cur.b] : null;
  const pillSubtext =
    cur.kind === 'approach' ? 'Approaching…' :
    cur.kind === 'enter'    ? 'Entering' :
    cur.kind === 'inside'   ? 'Inside — workflow in progress' :
    cur.kind === 'exit'     ? 'Exiting' :
    cur.kind === 'leave'    ? 'Journey complete · looping' : '';

  const handleClose = () => setActiveBuilding(null);
  const handleContinue = () => setActiveBuilding(null);
  const handleCta = (cta) => {
    if (onCtaClick) onCtaClick(cta);
    else window.location.href = cta.to;
  };

  const height = compact ? 380 : 520;

  return (
    <div
      style={{
        position: 'relative', width: '100%',
        borderRadius: 16, border: `1px solid ${C.border}`, overflow: 'hidden',
        background: C.skyTop,
        boxShadow: '0 6px 24px rgba(13, 31, 60, 0.06)',
      }}
      data-testid="journey-animation"
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height }}
        role="img"
        aria-label="Successlane workflow — five buildings on a main street. A character walks from left to right, entering each building's door in workflow order: Free Evaluation → Checkout → Expert Rewrite → Recruiter Certification (with balloons and confetti) → Delivery."
      >
        <defs>
          <linearGradient id="skyBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={C.skyTop} />
            <stop offset="100%" stopColor={C.skyBot} />
          </linearGradient>
          <linearGradient id="groundBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={C.groundTop} />
            <stop offset="100%" stopColor={C.groundBot} />
          </linearGradient>
        </defs>

        {/* Sky */}
        <rect x={0} y={0} width={VB_W} height={GROUND_Y} fill="url(#skyBg)" />
        {/* Sun + clouds */}
        <circle cx={1370} cy={75} r={22} fill="#FBBF24" opacity={0.85} />
        <circle cx={1370} cy={75} r={32} fill="#FBBF24" opacity={0.2} />
        <ellipse cx={150} cy={70}  rx={36} ry={10} fill={C.white} opacity={0.7} />
        <ellipse cx={180} cy={62}  rx={28} ry={9}  fill={C.white} opacity={0.8} />
        <ellipse cx={1170} cy={95} rx={32} ry={10} fill={C.white} opacity={0.7} />
        <ellipse cx={1200} cy={88} rx={26} ry={8}  fill={C.white} opacity={0.85} />

        {/* Distant mountains for depth */}
        <path d="M 0 460 L 100 410 L 220 450 L 360 400 L 500 440 L 640 405 L 800 445 L 960 415 L 1100 450 L 1280 425 L 1500 445 L 1500 560 L 0 560 Z" fill="#C9DDF0" opacity={0.5} />

        {/* Ground */}
        <rect x={0} y={GROUND_Y} width={VB_W} height={VB_H - GROUND_Y} fill="url(#groundBg)" />
        {/* Sidewalk */}
        <rect x={0} y={GROUND_Y} width={VB_W} height={ROAD_Y - GROUND_Y} fill={C.sidewalk} />
        <line x1={0} y1={GROUND_Y} x2={VB_W} y2={GROUND_Y} stroke={C.buildOutline} strokeWidth={1.4} />
        <line x1={0} y1={ROAD_Y}   x2={VB_W} y2={ROAD_Y}   stroke={C.roadEdge} strokeWidth={1.2} />
        {/* Road */}
        <rect x={0} y={ROAD_Y} width={VB_W} height={VB_H - ROAD_Y} fill={C.road} />
        {/* Centre dashes */}
        {[...Array(22)].map((_, i) => (
          <rect key={i} x={i * 75 + 8} y={ROAD_Y + 32} width={36} height={3} rx={1} fill={C.white} opacity={0.8} />
        ))}

        {/* ── SUCCESSLANE BILLBOARD next to Expert HQ ── */}
        <SuccesslaneBillboard />

        {/* ── BUILDINGS ── */}
        <EvalBuilding      doorOpen={doorOpen(0)} onClick={() => setActiveBuilding(BUILDINGS[0])} />
        <CheckoutBuilding  doorOpen={doorOpen(1)} onClick={() => setActiveBuilding(BUILDINGS[1])} />
        <ExpertBuilding    doorOpen={doorOpen(2)} onClick={() => setActiveBuilding(BUILDINGS[2])} />
        <RecruiterBuilding doorOpen={doorOpen(3)} celebrating={recruiterCeleb} onClick={() => setActiveBuilding(BUILDINGS[3])} />
        <DeliveryBuilding  doorOpen={doorOpen(4)} onClick={() => setActiveBuilding(BUILDINGS[4])} />

        {/* ── Recruiter celebration (balloons + confetti) ── */}
        <BalloonsAndConfetti visible={recruiterCeleb} />

        {/* ── STEP LABELS (fade in/out) ── */}
        {BUILDINGS.map((b, i) => (
          <StepLabel key={b.id} b={b} visible={labelVisible(i)} />
        ))}

        {/* ── CHARACTER ── */}
        {!prefersReducedMotion && (
          <motion.g
            animate={{ x: targetX, y: FEET_Y, opacity: charVisible ? 1 : 0 }}
            initial={{ x: 60, y: FEET_Y, opacity: 1 }}
            transition={{
              duration: cur.kind === 'approach' ? cur.dur / 1000 :
                        cur.kind === 'leave' ? cur.dur / 1000 :
                        cur.kind === 'enter' ? cur.dur / 1000 :
                        cur.kind === 'exit' ? cur.dur / 1000 :
                        0.4,
              ease: cur.kind === 'approach' ? 'linear' : 'easeInOut',
              opacity: { duration: 0.15 },
            }}
          >
            <Character walking={charWalking} />
          </motion.g>
        )}
        {prefersReducedMotion && (
          <g transform={`translate(${BUILDINGS[0].doorX - 60}, ${FEET_Y})`}>
            <Character walking={false} />
          </g>
        )}

        {/* ── CITY: trees, lamps, pedestrians, charging station, motorbikes, car ── */}
        {/* Trees scattered between buildings on sidewalk */}
        <PalmTree x={280}  y={GROUND_Y - 4} scale={0.9} />
        <PalmTree x={555}  y={GROUND_Y - 4} scale={0.9} />
        <RoundTree x={925} y={GROUND_Y - 4} scale={0.85} />
        <PalmTree x={1210} y={GROUND_Y - 4} scale={0.95} />
        <PalmTree x={1455} y={GROUND_Y - 4} scale={0.9} />
        <RoundTree x={45}  y={GROUND_Y - 4} scale={0.9} />

        {/* Street lamps */}
        <StreetLamp x={50}   y={GROUND_Y} />
        <StreetLamp x={300}  y={GROUND_Y} />
        <StreetLamp x={580}  y={GROUND_Y} />
        <StreetLamp x={950}  y={GROUND_Y} />
        <StreetLamp x={1220} y={GROUND_Y} />
        <StreetLamp x={1470} y={GROUND_Y} />

        {/* Pedestrians near shops / building entrances */}
        <Pedestrian x={70}  y={GROUND_Y + 30} color={C.ped1} scale={1.0} />
        <Pedestrian x={300} y={GROUND_Y + 28} color={C.ped3} scale={1.0} />
        <Pedestrian x={560} y={GROUND_Y + 26} color={C.ped2} scale={1.05} />
        <Pedestrian x={930} y={GROUND_Y + 30} color={C.ped5} scale={1.0} />
        <Pedestrian x={1215}y={GROUND_Y + 28} color={C.ped4} scale={1.0} />
        <Pedestrian x={1460}y={GROUND_Y + 28} color={C.ped1} scale={1.0} />
        {/* Group near Recruiter (loitering for the celebration) */}
        <Pedestrian x={1010} y={GROUND_Y + 50} color={C.ped3} scale={0.95} />
        <Pedestrian x={1145} y={GROUND_Y + 50} color={C.ped2} scale={0.95} />

        {/* Charging station + motorbikes NEAR THE ROAD */}
        <ChargingStation x={250} y={ROAD_Y - 5} />
        <Motorbike x={225} y={ROAD_Y - 2} color={C.red} />
        <Motorbike x={285} y={ROAD_Y - 2} color={C.primary} />

        <ChargingStation x={1190} y={ROAD_Y - 5} />
        <Motorbike x={1165} y={ROAD_Y - 2} color={C.gold} />
        <Motorbike x={1225} y={ROAD_Y - 2} color={C.ped5} />

        {/* Moving car along the road */}
        {!prefersReducedMotion && (
          <motion.g
            initial={{ x: -100 }} animate={{ x: VB_W + 100 }}
            transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          >
            <Car x={0} y={ROAD_Y + 65} color={C.primary} />
          </motion.g>
        )}
      </svg>

      {/* Step pill */}
      <div style={{
        position: 'absolute', top: 14, right: 14,
        background: C.white, border: `1px solid ${C.border}`,
        borderRadius: 99, padding: '5px 13px',
        fontSize: 11, fontWeight: 600, color: C.textMid,
        display: 'flex', alignItems: 'center', gap: 7,
        boxShadow: '0 2px 8px rgba(13, 31, 60, 0.08)',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: pillBuilding ? pillBuilding.accent : C.primary }} />
        {pillBuilding
          ? `Step ${pillBuilding.step} of 5 · ${pillBuilding.title}`
          : 'Successlane · workflow tour'}
        <span style={{ color: C.textFaint, fontWeight: 400 }}>· {pillSubtext}</span>
      </div>

      {!compact && (
        <div style={{
          position: 'absolute', bottom: 14, left: 18,
          fontSize: 12, color: C.textMid,
          background: 'rgba(255, 255, 255, 0.85)',
          padding: '4px 10px', borderRadius: 99,
          backdropFilter: 'blur(4px)',
        }}>
          Click any building to learn how it works
        </div>
      )}

      <InfoCard
        building={activeBuilding}
        onClose={handleClose}
        onContinue={handleContinue}
        onCta={handleCta}
      />
    </div>
  );
};

export default JourneyAnimation;
